<script setup>
import { inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from '@drama/ui'
import { NOVEL_STOREFRONT_LOCALES } from '@drama/contracts/novel'
import NovelImageField from './NovelImageField.vue'
import { authoringErrorKey } from '../../composables/novel/authoring-error.js'
import { NOVEL_COVER_LOCALES } from '../../composables/novel/cover-locales.js'

defineProps({ title: { type: String, default: '' } })
const covers = inject(NOVEL_COVER_LOCALES, null)
const { t } = useI18n()
const toast = useToast()

async function clear(locale) {
  try {
    await covers.clear(locale)
  } catch (error) {
    toast.error(t(authoringErrorKey(error, 'novelStudio.assets.localeCoverFailed')), { duration: 0 })
  }
}
</script>

<template>
  <details
    v-if="covers"
    class="novel-cover-locales"
    data-testid="novel-cover-locales-advanced"
  >
    <summary>{{ t('novelStudio.assets.advanced') }}</summary>
    <div
      class="novel-entity__nested"
      data-testid="novel-cover-locales"
    >
      <div class="novel-entity__nested-header">
        <h4>{{ t('novelStudio.assets.localeCovers') }}</h4>
      </div>
      <p class="novel-cover-locales__hint">
        {{ t('novelStudio.assets.localeCoversHint') }}
      </p>
      <NovelImageField
        v-for="locale in NOVEL_STOREFRONT_LOCALES"
        :key="locale"
        :model-value="covers.cover_locales.value[locale] || ''"
        :label="t('novelStudio.assets.localeCover', { language: t(`novelStudio.voices.languages.${locale}`) })"
        :tag="`cover-${locale}`"
        :upload="file => covers.upload(locale, file)"
        :alt="title"
        @update:model-value="url => { if (!url) clear(locale) }"
      />
    </div>
  </details>
</template>

<style scoped>
.novel-cover-locales { min-width: 0; }
.novel-cover-locales summary { padding: var(--drama-space-sm) 0; color: var(--drama-color-text); font-size: var(--drama-font-size-sm); font-weight: var(--drama-font-weight-medium); cursor: pointer; }
.novel-cover-locales summary:hover { color: var(--drama-color-primary); }
.novel-cover-locales summary:focus-visible { outline: 2px solid var(--drama-color-primary); outline-offset: 2px; border-radius: var(--drama-radius-sm); }
.novel-cover-locales__hint { margin: 0; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
</style>
